import { Icon } from '@iconify/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { type Optional } from 'utility-types';
import { type Languages } from '@/Domain/components/Settings/ai/LANGUAGES';
import { BoxIcon } from '@/Domain/components/Shared/BoxIcon/BoxIcon';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/Domain/components/Shared/Field/Field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { useAutoSubmit } from '@/Domain/hooks/App/useAutoSave.hook';

enum PersonalityEnum {
  'normal' = 0,
  'humorous' = 1,
  'professional' = 2,
  'friendly' = 3,
  'sassy' = 4,
  'intelligent' = 5,
  'empathetic' = 6,
  'bold' = 7,
  'excited' = 9,
  'mysterious' = 10,
  'inspiring_motivational' = 11,
  'adventurous' = 12,
  'sophisticated_elegant' = 13,
}

const Professions = [
  'helpful_assistant',
  'customer_support_representative',
  'shopping_assistant',
  'field_expert',
  'interviewer',
];

enum CreativityEnum {
  LOW = 0,
  MEDIUM = 1,
  HIGH = 2,
}

enum ReplyEnum {
  LOW = 0,
  MEDIUM = 1,
  HIGH = 2,
}

enum LinksPriorityEnum {
  MEDIUM = 0,
  HIGH = 1,
}

export interface AISettings {
  languages: Languages[];
  personality: PersonalityEnum;
  profession: string;
  creativity_level: CreativityEnum;
  reply_length: ReplyEnum;
  url_priority: LinksPriorityEnum;
  messages_limit: number;
  voice: {
    active: boolean;
    type: string | undefined;
  };
  excluded: string[];
}

type Props = {
  data: AISettings;
};

const formId = 'settings-ai';
export const ConversationStylePage = ({ data }: Props) => {
  const { t } = useTranslation(['settings', 'common']);
  const queryClient = useQueryClient();
  const location = useLocation();

  const form = useForm<AISettings>({ values: data as AISettings });
  const { control } = form;

  const mutation = useMutation<Partial<AISettings>, unknown, Optional<AISettings>>({
    mutationKey: ['settings'],
    onMutate: (updatedData) => {
      queryClient.setQueryData(['settings'], () => ({ ...data, ...updatedData }));
    },
    onError: (_, prevData) => {
      queryClient.setQueryData(['settings'], () => prevData);
    },
  });

  useAutoSubmit<AISettings>(form, (nextData) => mutation.mutate(nextData));

  const { getConfig, isActivated, isFetched } = useSubscription();
  const aiPreferencesLocked = isFetched && !isActivated('ai_preferences');
  const personalities = (getConfig('personalities') as Record<string, boolean>) || {};
  const enabledPersonalities = (
    Object.keys(personalities) as Array<keyof typeof PersonalityEnum>
  ).map((personality) => PersonalityEnum[personality]);

  const enabledProfessions = (getConfig('professions') as Record<string, boolean>) || {};

  useEffect(() => {
    if (location.state?.scrollTo === 'messagesLimit') {
      const element = document.getElementById('messages-limit-card-body');

      if (element != null) {
        const offsetTop = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetToScroll = offsetTop - 75;

        window.scrollTo({
          top: offsetToScroll,
          behavior: 'smooth',
        });
      }
    }
  }, [location]);

  return (
    <FormProvider {...form}>
      <div className="border-0">
        <form id={formId} />
        <FieldGroup data-testid="conversation-style-section">
          <Field>
            <FieldLabel>{t('personality.label')}</FieldLabel>
            <FieldDescription>{t('personality.description')}</FieldDescription>
            <Controller
              name="personality"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Select
                    value={String(field.value)}
                    onValueChange={(val) => field.onChange(Number(val))}
                  >
                    <SelectTrigger id={`ai.${field.name}`} data-testid="personality-select">
                      <SelectValue placeholder={t('personality.label')} />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(PersonalityEnum)
                        .map((x) => parseInt(x as string, 10))
                        .filter((x) => !isNaN(x))
                        .map((value) => (
                          <SelectItem
                            key={value}
                            value={String(value)}
                            disabled={!enabledPersonalities.includes(value)}
                          >
                            {t(`personality.options.${value}`)}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FieldError>{error?.message}</FieldError>
                  <p className="mt-4 text-sm text-foreground-subtle">
                    {t(`personality.descriptions.${field.value}`)}
                  </p>
                </>
              )}
            />
          </Field>
          <Field>
            <FieldLabel>{t('profession.label')}</FieldLabel>
            <FieldDescription>{t('profession.description')}</FieldDescription>
            <Controller
              name="profession"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <div className="my-2 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-5">
                    {Object.values(Professions).map((value) => {
                      const isDisabled = !enabledProfessions[value];
                      const isActive = field.value === value;
                      return (
                        <div
                          key={value}
                          className={classNames(
                            'flex cursor-pointer flex-col justify-between rounded-xl border border-border p-4 text-center hover:bg-accent',
                            {
                              'bg-accent': isActive,
                              'cursor-not-allowed opacity-50': isDisabled,
                            },
                          )}
                          onClick={() => {
                            if (!isDisabled) {
                              field.onChange(value);
                            }
                          }}
                        >
                          <div className="flex h-full flex-col items-center justify-between">
                            <span className="text-sm font-medium text-foreground">
                              {t(`profession.options.${value}`)}
                            </span>
                            <span
                              className={classNames(
                                'my-2 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium',
                                {
                                  invisible: !isActive,
                                  'bg-card text-foreground': isActive,
                                },
                              )}
                            >
                              {t('common:active')}
                            </span>
                            <p
                              className={classNames('mb-0 mt-4 text-xs', {
                                'text-foreground-subtle': isActive,
                                'text-foreground-subtle hover:!text-foreground-subtle': !isActive,
                              })}
                            >
                              {t(`profession.descriptions.${value}`)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <FieldError>{error?.message}</FieldError>
                </>
              )}
            />
          </Field>
          <Field>
            <FieldLabel>{t('creativity.label')}</FieldLabel>
            <FieldDescription>{t('creativity.description')}</FieldDescription>
            <Controller
              name="creativity_level"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <div className="relative">
                  <div className="flex w-full gap-1 rounded-xl border border-border p-1">
                    {Object.values(CreativityEnum)
                      .map((x) => parseInt(x as string, 10))
                      .filter((x) => !isNaN(x))
                      .map((value) => {
                        const isActive = field.value === value;
                        const isDisabled = aiPreferencesLocked;
                        return (
                          <div
                            key={value}
                            className={classNames(
                              'flex min-h-[86px] flex-1 cursor-pointer flex-col justify-center rounded-lg px-2 py-2 sm:px-4 sm:py-3',
                              {
                                'bg-accent': isActive,
                                'hover:bg-accent': !isActive,
                                'cursor-not-allowed opacity-50': isDisabled,
                              },
                            )}
                            onClick={() => {
                              if (!aiPreferencesLocked) {
                                field.onChange(value);
                              }
                            }}
                          >
                            <div className="text-sm font-medium">
                              {t(`creativity.options.${value}`)}
                            </div>
                            <div
                              className={classNames('text-xs', {
                                'text-foreground-subtle': isActive,
                                'text-foreground-subtle hover:text-foreground-subtle': !isActive,
                              })}
                            >
                              {t(`creativity.descriptions.${value}`)}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                  {aiPreferencesLocked && (
                    <div className="absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center gap-2 rounded-xl transition-all duration-300 hover:bg-foreground-subtle/10 hover:backdrop-blur-sm group">
                      <BoxIcon variant="outlined" className="mx-auto hidden group-hover:block">
                        <Icon
                          icon="mingcute:calendar-time-add-line"
                          className="mx-auto h-full text-foreground-subtle"
                        />
                      </BoxIcon>
                      <Trans
                        i18nKey="common:upgrade_package"
                        t={t}
                        components={{
                          upgrade: (
                            <Link
                              to="../subscription"
                              className="hidden text-xs font-medium text-foreground group-hover:block"
                            />
                          ),
                        }}
                      />
                    </div>
                  )}
                  <FieldError>{error?.message}</FieldError>
                </div>
              )}
            />
          </Field>
          <Field>
            <FieldLabel>{t('reply.label')}</FieldLabel>
            <FieldDescription>{t('reply.description')}</FieldDescription>
            <Controller
              name="reply_length"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <div className="relative">
                  <div className="flex w-full gap-1 rounded-xl border border-border p-1">
                    {Object.values(ReplyEnum)
                      .map((x) => parseInt(x as string, 10))
                      .filter((x) => !isNaN(x))
                      .map((value) => {
                        const isActive = field.value === value;
                        const isDisabled = aiPreferencesLocked;
                        return (
                          <div
                            key={value}
                            className={classNames(
                              'flex min-h-[86px] flex-1 cursor-pointer flex-col justify-center rounded-lg px-2 py-2 sm:px-4 sm:py-3',
                              {
                                'bg-accent': isActive,
                                'hover:bg-accent': !isActive,
                                'cursor-not-allowed opacity-50': isDisabled,
                              },
                            )}
                            onClick={() => {
                              if (!aiPreferencesLocked) {
                                field.onChange(value);
                              }
                            }}
                          >
                            <div className="text-sm font-medium">{t(`reply.options.${value}`)}</div>
                            <div
                              className={classNames('text-xs', {
                                'text-foreground-subtle': isActive,
                                'text-foreground-subtle hover:text-foreground-subtle': !isActive,
                              })}
                            >
                              {t(`reply.descriptions.${value}`)}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                  {aiPreferencesLocked && (
                    <div className="absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center gap-2 rounded-xl transition-all duration-300 hover:bg-foreground-subtle/10 hover:backdrop-blur-sm group">
                      <BoxIcon variant="outlined" className="mx-auto hidden group-hover:block">
                        <Icon
                          icon="mingcute:calendar-time-add-line"
                          className="mx-auto h-full text-foreground-subtle"
                        />
                      </BoxIcon>
                      <Trans
                        i18nKey="common:upgrade_package"
                        t={t}
                        components={{
                          upgrade: (
                            <Link
                              to="../subscription"
                              className="hidden text-xs font-medium text-foreground group-hover:block"
                            />
                          ),
                        }}
                      />
                    </div>
                  )}
                  <FieldError>{error?.message}</FieldError>
                </div>
              )}
            />
          </Field>
          <Field>
            <FieldLabel>{t('links_priority.label')}</FieldLabel>
            <FieldDescription>{t('links_priority.description')}</FieldDescription>
            <Controller
              name="url_priority"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <div className="relative">
                  <div
                    className={classNames('flex w-full gap-1 rounded-xl border border-border p-1', {
                      'cursor-not-allowed opacity-50': aiPreferencesLocked,
                    })}
                  >
                    {Object.values(LinksPriorityEnum)
                      .map((x) => parseInt(x as string, 10))
                      .filter((x) => !isNaN(x))
                      .map((value) => {
                        const isActive = field.value === value;
                        return (
                          <div
                            key={value}
                            className={classNames(
                              'flex min-h-[86px] w-full flex-col items-start gap-2 rounded-lg p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:p-2',
                              {
                                'bg-accent': isActive,
                                'cursor-pointer hover:bg-accent': !isActive,
                                'pointer-events-none': aiPreferencesLocked,
                              },
                            )}
                            onClick={() => {
                              if (!aiPreferencesLocked) {
                                field.onChange(value);
                              }
                            }}
                          >
                            <div className="flex items-center">
                              <input
                                type="radio"
                                id={`url_priority_${value}`}
                                value={value}
                                checked={isActive}
                                disabled={aiPreferencesLocked}
                                className="form-radio h-5 w-5 accent-black"
                              />
                              <span className="ml-2 text-sm font-medium">
                                {t(`links_priority.options.${value}`)}
                              </span>
                            </div>
                            <p
                              className={classNames('mb-0 text-xs sm:px-4', {
                                'text-foreground-subtle': isActive,
                                'text-foreground-subtle hover:!text-foreground-subtle': !isActive,
                              })}
                            >
                              {t(`links_priority.options_description.${value}`)}
                            </p>
                          </div>
                        );
                      })}
                  </div>
                  {aiPreferencesLocked && (
                    <div className="absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center gap-2 rounded-xl transition-all duration-300 hover:bg-foreground-subtle/10 hover:backdrop-blur-sm group">
                      <BoxIcon variant="outlined" className="mx-auto hidden group-hover:block">
                        <Icon
                          icon="mingcute:calendar-time-add-line"
                          className="mx-auto h-full text-foreground-subtle"
                        />
                      </BoxIcon>
                      <Trans
                        i18nKey="common:upgrade_package"
                        t={t}
                        components={{
                          upgrade: (
                            <Link
                              to="../subscription"
                              className="hidden text-xs font-medium text-foreground group-hover:block"
                            />
                          ),
                        }}
                      />
                    </div>
                  )}
                  <FieldError>{error?.message}</FieldError>
                </div>
              )}
            />
          </Field>
        </FieldGroup>
      </div>
    </FormProvider>
  );
};
